'use client'

import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { DcChart, colors } from 'pixel-base'
import { useDashboardCollectionsData } from './useDashboardData'

interface TopCollection {
  collection_name: string
  total_articles_sold: number
  total_revenue: number
  unique_products_count: number
  total_orders: number
}

interface TopCollectionsChartProps {
  filters: {
    start_date: Date | string
    end_date: Date | string
    data_type: string
  }
  height?: number | string
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
}

const TopCollectionsChart: React.FC<TopCollectionsChartProps> = ({
  filters,
  height = 350,
  setComponent,
  setShowDetail,
}) => {
  // Add state for toggle functionality
  const [activeToggle, setActiveToggle] = useState<number>(0)

  // Use the shared hook instead of direct API call
  const { topCollections: data, loading } = useDashboardCollectionsData(filters)

  const formattedData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        yAxis: [],
        series: [],
      }
    }

    // Sort data by total articles sold (highest first)
    const sortedData = [...data].sort(
      (a, b) => b.total_articles_sold - a.total_articles_sold,
    )

    // Extract values for chart based on active toggle
    const collections = sortedData.map((item) => item.collection_name)
    let chartData: number[]

    if (activeToggle === 0) {
      // Articles Sold
      chartData = sortedData.map((item) => item.total_articles_sold)
    } else {
      // Unique Products
      chartData = sortedData.map((item) => item.unique_products_count)
    }

    return {
      yAxis: collections,
      series: [
        {
          name: activeToggle === 0 ? 'Articles Sold' : 'Unique Products',
          type: 'bar',
          data: chartData,
          itemStyle: { color: colors?.primary || '#5f38f9' },
          label: {
            show: true,
            position: 'insideLeft',
            formatter: (params: any) => {
              const index = params.dataIndex
              const collection = collections[index] || ''
              // Truncate long collection names to fit in the bar
              return collection.length > 25
                ? collection.substring(0, 25) + '...'
                : collection
            },
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 'bold',
            padding: [4, 8],
            width: 180,
            overflow: 'truncate',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
          },
          barWidth: '80%', // Make bars wider
        },
      ],
    }
  }, [data, activeToggle])

  const totalArticlesSold = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce((sum, item) => sum + (item.total_articles_sold || 0), 0)
  }, [data])

  const totalUniqueProducts = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce(
      (sum, item) => sum + (item.unique_products_count || 0),
      0,
    )
  }, [data])

  const chartOptions = {
    legend: {
      orient: 'horizontal',
      top: 25,
      left: 'center',
      padding: [0, 10, 10, 10],
      textStyle: {
        fontSize: 12,
        color: '#333',
      },
    },
    grid: {
      top: 60,
      bottom: 30,
      left: 120, // Increased left margin for collection names
      right: 60,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        axis: 'y',
        snap: true,
        lineStyle: {
          color: '#5f38f9',
          width: 1,
        },
      },
      position: 'top',
      backgroundColor: '#ffffff',
      borderColor: '#e0e0e0',
      borderWidth: 1,
      extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);',
      textStyle: {
        color: '#333333',
      },
      formatter: (params: any) => {
        if (!params || !Array.isArray(params) || params.length === 0) return ''

        const collection = params[0].axisValue
        let html = `
          <div style="margin-bottom: 8px;">
            <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px;">${collection}</div>
            <div style="height: 1px; background: #e0e0e0; margin: 8px 0;"></div>
          </div>
        `

        // Add each data series
        params.forEach((param: any) => {
          const value = param.value || 0
          const seriesName = param.seriesName

          html += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px; align-items: center;">
              <div style="display: flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #5f38f9; margin-right: 5px;"></span>
                ${seriesName}:
              </div>&nbsp;&nbsp;
              <div style="font-weight: 500;">${value.toLocaleString()}</div>
            </div>
          `
        })

        // If this is the collection data, add the unique products count
        const collectionData = data.find(
          (c) => c.collection_name === collection,
        )
        if (collectionData) {
          html += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px; align-items: center;">
              <div style="display: flex; align-items: center;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: #f2bc16; margin-right: 5px;"></span>
                Unique Products:
              </div>&nbsp;&nbsp;
              <div style="font-weight: 500;">${collectionData.unique_products_count.toLocaleString()}</div>
            </div>
          `
        }

        return html
      },
    },
    xAxis: {
      show: false, // Hide x-axis labels
    },
  }

  return (
    <DcChart
      isLoading={loading || !data || data.length === 0}
      toggleData={[
        {
          title: 'Total Articles Sold',
          data: totalArticlesSold.toLocaleString(),
        },
        {
          title: 'Total Unique Products',
          data: totalUniqueProducts.toLocaleString(),
        },
      ]}
      title="Top Collections"
      isTitleClickable={true}
      onClickTitle={() => {
        setComponent('topCollectionsChart')
        setShowDetail(true)
      }}
      onToggleDataClick={(index) => {
        setActiveToggle(index)
      }}
      activeToggle={activeToggle}
      type="horizontalBar"
      data={formattedData}
      height={height}
      options={chartOptions}
    />
  )
}

export default TopCollectionsChart
